import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { http, formatApiErrorDetail } from "@/lib/api";
import { formatIDR, formatShortIDR, formatPercent, periodLabel, currentPeriod, periodShort } from "@/lib/format";
import PeriodPicker from "@/components/PeriodPicker";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ArrowUpRight, ArrowDownRight, Wallet, PieChart as PieIcon, TrendingUp, Target, Pencil } from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
  Area,
  AreaChart,
} from "recharts";

const fetcher = (url) => http.get(url).then((r) => r.data);

// Constants extracted to avoid re-creation on each render
const CHART_MARGIN = { top: 10, right: 12, left: -8, bottom: 0 };
const AXIS_TICK = { fill: "#64748B", fontSize: 11 };
const TOOLTIP_CONTENT_STYLE = {
  borderRadius: 8,
  border: "1px solid #E2E8F0",
  boxShadow: "0 4px 12px rgba(15,23,42,0.08)",
};
const LEGEND_STYLE = { fontSize: 12 };
const DOT_STYLE = { r: 3, fill: "#2563EB" };
const formatIDRTooltip = (v) => formatIDR(v);

export default function Dashboard() {
  const [period, setPeriod] = useState(currentPeriod());
  const { data, mutate } = useSWR(`/dashboard/summary?period=${period}`, fetcher);
  const year = parseInt(period.split("-")[0], 10);
  const { data: goal, mutate: mutGoal } = useSWR(`/goals/${year}`, fetcher);

  const [goalOpen, setGoalOpen] = useState(false);
  const [goalRaw, setGoalRaw] = useState("");

  useEffect(() => { mutate(); }, [period, mutate]);
  useEffect(() => {
    const v = goal?.target_amount || 0;
    setGoalRaw(v ? Number(v).toLocaleString("id-ID") : "");
  }, [goal]);

  const saveGoal = async () => {
    const digits = (goalRaw || "").replace(/\D/g, "");
    const amount = digits ? parseInt(digits, 10) : 0;
    try {
      await http.put("/goals", { year, target_amount: amount });
      toast.success(`Target ${year} disimpan`);
      setGoalOpen(false);
      mutGoal();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const target = Number(goal?.target_amount || 0);
  const current = Number(data?.total || 0);
  const progressPct = target > 0 ? Math.min(100, (current / target) * 100) : 0;
  const remaining = Math.max(0, target - current);

  const isPositive = (data?.growth_pct ?? 0) >= 0;

  const trend = useMemo(
    () => (data?.trend || []).map((t) => ({ ...t, label: periodShort(t.period) })),
    [data]
  );

  const allocation = data?.allocation || [];
  const totalAlloc = allocation.reduce((a, b) => a + b.value, 0);
  const allocationSorted = useMemo(
    () => [...allocation].sort((a, b) => b.value - a.value),
    [allocation]
  );

  return (
    <div className="space-y-8">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Dashboard</div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            Ringkasan Kekayaan
          </h1>
          <p className="mt-1 text-slate-600">Periode: <span className="font-medium text-slate-800">{periodLabel(period)}</span></p>
        </div>
        <PeriodPicker period={period} onChange={setPeriod} testidPrefix="dashboard-period" />
      </header>

      {/* Metric cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        <Card className="p-6 card-hover" data-testid="metric-total-card">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold tracking-[0.1em] uppercase text-slate-500">Total Aset</div>
            <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 grid place-items-center"><Wallet className="w-4 h-4" /></div>
          </div>
          <div className="mt-4 font-display text-4xl lg:text-5xl font-bold tracking-tighter text-slate-900 tabular" data-testid="metric-total-value">
            {formatIDR(data?.total)}
          </div>
          <div className="mt-2 text-xs text-slate-500">Nilai bersih pada {periodLabel(period)}</div>
        </Card>

        <Card className="p-6 card-hover" data-testid="metric-growth-card">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold tracking-[0.1em] uppercase text-slate-500">Pertumbuhan Bulanan</div>
            <div className={`w-9 h-9 rounded-lg grid place-items-center ${isPositive ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
              {isPositive ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
            </div>
          </div>
          <div className={`mt-4 font-display text-4xl lg:text-5xl font-bold tracking-tighter tabular ${isPositive ? "text-emerald-600" : "text-rose-600"}`} data-testid="metric-growth-value">
            {formatPercent(data?.growth_pct)}
          </div>
          <div className="mt-2 text-xs text-slate-500 tabular">
            {isPositive ? "+" : ""}{formatIDR(data?.delta)} vs bulan sebelumnya
          </div>
        </Card>

        <Card className="p-6 card-hover" data-testid="metric-prev-card">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold tracking-[0.1em] uppercase text-slate-500">Total Bulan Lalu</div>
            <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-600 grid place-items-center"><TrendingUp className="w-4 h-4" /></div>
          </div>
          <div className="mt-4 font-display text-3xl lg:text-4xl font-semibold tracking-tighter text-slate-700 tabular">
            {formatIDR(data?.total_prev)}
          </div>
          <div className="mt-2 text-xs text-slate-500">Perbandingan periode</div>
        </Card>
      </div>

      {/* Target Progress */}
      <Card className="p-6 card-hover" data-testid="target-progress-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 grid place-items-center">
              <Target className="w-5 h-5" strokeWidth={1.75} />
            </div>
            <div>
              <div className="text-xs font-semibold tracking-[0.1em] uppercase text-slate-500">Target Net Worth {year}</div>
              <div className="mt-0.5 font-display text-2xl font-semibold tracking-tight text-slate-900 tabular" data-testid="target-value">
                {target > 0 ? formatIDR(target) : "Belum ditetapkan"}
              </div>
            </div>
          </div>
          <Dialog open={goalOpen} onOpenChange={setGoalOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                className="border-slate-200 gap-2"
                data-testid="edit-target-button"
              >
                <Pencil className="w-4 h-4" /> {target > 0 ? "Ubah target" : "Set target"}
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="target-modal">
              <DialogHeader>
                <DialogTitle>Target Net Worth {year}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Target (IDR)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">Rp</span>
                    <Input
                      className="pl-9 h-10 tabular text-right"
                      inputMode="numeric"
                      value={goalRaw}
                      data-testid="target-input"
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, "");
                        const n = digits ? parseInt(digits, 10) : 0;
                        setGoalRaw(n ? n.toLocaleString("id-ID") : "");
                      }}
                      placeholder="0"
                    />
                  </div>
                  <p className="mt-2 text-xs text-slate-500">Target berlaku untuk semua bulan di tahun {year}.</p>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={saveGoal} className="bg-blue-600 hover:bg-blue-700" data-testid="target-save-button">
                  Simpan
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {target > 0 && (
          <div className="mt-5">
            <div className="flex items-end justify-between text-sm mb-2">
              <div>
                <span className="font-display text-xl font-semibold text-slate-900 tabular">{progressPct.toFixed(1)}%</span>
                <span className="ml-2 text-slate-500">tercapai</span>
              </div>
              <div className="text-slate-500 tabular text-xs">
                Sisa: <span className="text-slate-800 font-medium">{formatIDR(remaining)}</span>
              </div>
            </div>
            <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
              <div
                data-testid="target-progress-bar"
                className={`h-full transition-[width] duration-500 ${progressPct >= 100 ? "bg-emerald-500" : "bg-blue-600"}`}
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <div className="mt-2 flex justify-between text-[11px] text-slate-500 tabular">
              <span>{formatShortIDR(current)}</span>
              <span>{formatShortIDR(target)}</span>
            </div>
          </div>
        )}
      </Card>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        <Card className="p-6 lg:col-span-2 card-hover" data-testid="trend-chart-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs font-semibold tracking-[0.1em] uppercase text-slate-500">Tren Net Worth</div>
              <h3 className="mt-0.5 font-display text-xl font-medium text-slate-900">12 bulan terakhir</h3>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer>
              <AreaChart data={trend} margin={CHART_MARGIN}>
                <defs>
                  <linearGradient id="gArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2563EB" stopOpacity={0.28} />
                    <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="label" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={formatShortIDR} tick={AXIS_TICK} axisLine={false} tickLine={false} width={70} />
                <Tooltip formatter={formatIDRTooltip} contentStyle={TOOLTIP_CONTENT_STYLE} />
                <Area type="monotone" dataKey="total" stroke="#2563EB" strokeWidth={2.4} fill="url(#gArea)" />
                <Line type="monotone" dataKey="total" stroke="#2563EB" strokeWidth={0} dot={DOT_STYLE} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 card-hover" data-testid="allocation-chart-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs font-semibold tracking-[0.1em] uppercase text-slate-500">Alokasi Aset</div>
              <h3 className="mt-0.5 font-display text-xl font-medium text-slate-900">Per Kategori</h3>
            </div>
            <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 grid place-items-center"><PieIcon className="w-4 h-4" /></div>
          </div>
          {totalAlloc === 0 ? (
            <div className="h-64 grid place-items-center text-sm text-slate-500 border border-dashed border-slate-200 rounded-md">
              Belum ada data untuk periode ini
            </div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={allocation} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                    {allocation.map((a) => (
                      <Cell key={a.category_id} fill={a.color || "#94A3B8"} />
                    ))}
                  </Pie>
                  <Tooltip formatter={formatIDRTooltip} />
                  <Legend wrapperStyle={LEGEND_STYLE} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>

      {/* Category breakdown table */}
      <Card className="p-6 card-hover" data-testid="breakdown-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs font-semibold tracking-[0.1em] uppercase text-slate-500">Rincian Alokasi</div>
            <h3 className="mt-0.5 font-display text-xl font-medium text-slate-900">Kontribusi tiap kategori</h3>
          </div>
        </div>
        <div className="divide-y divide-slate-100">
          {allocation.length === 0 && (
            <div className="text-sm text-slate-500 py-6">Tidak ada rincian.</div>
          )}
          {allocationSorted.map((a) => {
            const pct = totalAlloc > 0 ? (a.value / totalAlloc) * 100 : 0;
            return (
              <div key={a.category_id} className="py-3 flex items-center gap-4">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: a.color }} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{a.name}</div>
                  <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full" style={{ width: `${pct}%`, background: a.color }} />
                  </div>
                </div>
                <div className="text-right w-40">
                  <div className="font-medium text-slate-900 tabular">{formatIDR(a.value)}</div>
                  <div className="text-xs text-slate-500 tabular">{pct.toFixed(1)}%</div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
